import React, { forwardRef } from 'react';
import { FieldError } from 'react-hook-form';

interface FormSwitchProps {
  label?: string;
  description?: string;
  error?: FieldError | string;
  required?: boolean;
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  id?: string;
}

const FormSwitch = forwardRef<HTMLInputElement, FormSwitchProps>(
  (
    {
      label,
      description,
      error,
      required,
      checked = false,
      onChange,
      disabled = false,
      size = 'md',
      className = '',
      id,
      ...props
    },
    ref
  ) => {
    const inputId = id || `switch-${Math.random().toString(36).substr(2, 9)}`;
    const errorMessage = typeof error === 'string' ? error : error?.message;
    const hasError = !!errorMessage;

    const sizeConfig = {
      sm: {
        track: 'w-8 h-4',
        thumb: 'w-3 h-3',
        translate: 'translate-x-4',
      },
      md: {
        track: 'w-11 h-6',
        thumb: 'w-5 h-5',
        translate: 'translate-x-5',
      },
      lg: {
        track: 'w-14 h-8',
        thumb: 'w-7 h-7',
        translate: 'translate-x-6',
      },
    }[size];

    const handleToggle = () => {
      if (!disabled) {
        onChange?.(!checked);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        handleToggle();
      }
    };

    return (
      <div className={`space-y-1 ${className}`}>
        <div className="flex items-start space-x-3">
          {/* Switch Container */}
          <div className="flex items-center">
            {/* Hidden Input */}
            <input
              ref={ref}
              type="checkbox"
              id={inputId}
              checked={checked}
              onChange={e => onChange?.(e.target.checked)}
              disabled={disabled}
              className="sr-only"
              aria-invalid={hasError}
              aria-describedby={
                errorMessage
                  ? `${inputId}-error`
                  : description
                    ? `${inputId}-description`
                    : undefined
              }
              {...props}
            />

            {/* Visual Switch */}
            <button
              type="button"
              role="switch"
              aria-checked={checked}
              aria-labelledby={label ? `${inputId}-label` : undefined}
              aria-describedby={
                errorMessage
                  ? `${inputId}-error`
                  : description
                    ? `${inputId}-description`
                    : undefined
              }
              onClick={handleToggle}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              className={`
                relative inline-flex items-center ${sizeConfig.track} rounded-full
                transition-colors duration-200 ease-in-out
                focus:outline-none focus:ring-2 focus:ring-brand-400 focus:ring-offset-2
                ${checked ? 'bg-brand-primary' : 'bg-gray-200'}
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                ${hasError ? 'ring-2 ring-red-400' : ''}
              `}
            >
              {/* Thumb */}
              <span
                className={`
                  ${sizeConfig.thumb} inline-block rounded-full bg-white shadow transform
                  transition-transform duration-200 ease-in-out
                  ${checked ? sizeConfig.translate : 'translate-x-0.5'}
                `}
              />
            </button>
          </div>

          {/* Label and Description */}
          {(label || description) && (
            <div className="flex-1 min-w-0">
              {label && (
                <label
                  id={`${inputId}-label`}
                  htmlFor={inputId}
                  className={`
                    block text-body-sm font-medium cursor-pointer
                    ${disabled ? 'text-gray-400' : 'text-gray-900'}
                  `}
                >
                  {label}
                  {required && <span className="text-red-500 ml-1">*</span>}
                </label>
              )}

              {description && (
                <p
                  id={`${inputId}-description`}
                  className={`
                    text-meta mt-0.5
                    ${disabled ? 'text-gray-400' : 'text-gray-500'}
                  `}
                >
                  {description}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Error Message */}
        {hasError && (
          <p id={`${inputId}-error`} className="text-meta text-red-600 ml-14" role="alert">
            {errorMessage}
          </p>
        )}
      </div>
    );
  }
);

FormSwitch.displayName = 'FormSwitch';

export default FormSwitch;
